import { SonosSpeaker } from '../installed/music';
import { SamsungDevice } from '../installed/samsung';
import { AndroidTvDevice } from '../installed/androidtv';
import { LgDevice } from '../installed/air';
import { EvohomeDevice } from '../installed/heat';
import { PiletaDevice } from '../installed/pileta';

export type RoomListItem =
  | { kind: 'device'; key: number | string; orden: number; data: any }
  | { kind: 'sonos'; key: string; orden: number; data: SonosSpeaker }
  | { kind: 'samsung'; key: string; orden: number; data: SamsungDevice }
  | { kind: 'androidtv'; key: string; orden: number; data: AndroidTvDevice }
  | { kind: 'lg'; key: string; orden: number; data: LgDevice }
  | { kind: 'evohome'; key: string; orden: number; data: EvohomeDevice }
  | { kind: 'pileta'; key: string; orden: number; data: PiletaDevice };

const ORDEN_SIN_ASIGNAR = Number.MAX_SAFE_INTEGER;

export type RoomSortCriterion =
  | 'defecto'
  | 'personalizado'
  | 'consumo-mayor'
  | 'consumo-menor'
  | 'estado'
  | 'artefacto'
  | 'nombre';

/** Clave estable para guardar el orden de una persona. */
export function roomOrderKey(item: { kind: string; data: { id: number } }): string {
  const prefix = item.kind === 'device' ? 'shelly' : item.kind;
  return `${prefix}:${item.data.id}`;
}

function itemName(item: RoomListItem): string {
  const data = item.data as { nombre?: string; display_name?: string };
  return String(data.nombre || data.display_name || '');
}

function itemConsumo(item: RoomListItem): number {
  if (item.kind !== 'device') return 0;
  const value = Number(item.data?.consumo);
  return Number.isFinite(value) ? value : 0;
}

function itemIsOn(item: RoomListItem): boolean {
  const data = item.data as any;
  if (item.kind === 'device') {
    if (data.tipo === 'PLUS2PM') {
      return String(data.estado) === 'opening' || String(data.estado) === 'closing';
    }
    if (data.tipo === 'DIMMER2') {
      return data.estado === true || (typeof data.estado === 'number' && data.estado > 0);
    }
    return Boolean(data.estado);
  }
  if (item.kind === 'sonos') return data.playback_state === 'playing';
  if (item.kind === 'samsung' || item.kind === 'lg') return data.power === true;
  if (item.kind === 'androidtv') return data.is_on === true;
  if (item.kind === 'evohome') return data.asking_heat === true;
  if (item.kind === 'pileta') {
    const pileta = data.chips_state?.pileta?.active?.length || 0;
    const bomba = data.chips_state?.bomba?.active?.length || 0;
    return pileta + bomba > 0;
  }
  return false;
}

function itemArtefacto(item: RoomListItem): string {
  const data = item.data as {
    artefacto?: { nombre?: string | null } | null;
    tipo_artefacto?: string | null;
    artefacto_nombre?: string | null;
  };
  return String(data.artefacto?.nombre || data.tipo_artefacto || data.artefacto_nombre || '').trim();
}

function sortByAdminOrden(items: RoomListItem[]): RoomListItem[] {
  const hasCustomOrder = items.some((i) => i.orden !== ORDEN_SIN_ASIGNAR);
  if (!hasCustomOrder) {
    return items.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'device' ? -1 : 1));
  }
  return items.sort((a, b) => {
    if (a.orden !== b.orden) return a.orden - b.orden;
    return a.kind === b.kind ? 0 : a.kind === 'device' ? -1 : 1;
  });
}

function sortByUserOrden(items: RoomListItem[], userOrder: ReadonlyMap<string, number>): RoomListItem[] {
  return items.sort((a, b) => {
    const ra = userOrder.has(roomOrderKey(a)) ? userOrder.get(roomOrderKey(a))! : ORDEN_SIN_ASIGNAR;
    const rb = userOrder.has(roomOrderKey(b)) ? userOrder.get(roomOrderKey(b))! : ORDEN_SIN_ASIGNAR;
    if (ra !== rb) return ra - rb;
    if (a.orden !== b.orden) return a.orden - b.orden;
    return itemName(a).localeCompare(itemName(b), 'es');
  });
}

/** Ordena todas las tarjetas de la habitación. Sin orden propio, usa el de Ajustes. */
export function sortRoomItems(
  items: RoomListItem[],
  criterion: RoomSortCriterion,
  userOrder: ReadonlyMap<string, number> | null,
): RoomListItem[] {
  const list = [...items];
  if (criterion === 'defecto') {
    return sortByAdminOrden(list);
  }
  if (criterion === 'personalizado') {
    if (userOrder && userOrder.size > 0) return sortByUserOrden(list, userOrder);
    return sortByAdminOrden(list);
  }
  if (criterion === 'consumo-mayor' || criterion === 'consumo-menor') {
    const dir = criterion === 'consumo-mayor' ? -1 : 1;
    return list.sort((a, b) => {
      const diff = itemConsumo(a) - itemConsumo(b);
      if (diff !== 0) return diff * dir;
      return itemName(a).localeCompare(itemName(b), 'es');
    });
  }
  if (criterion === 'estado') {
    return list.sort((a, b) => {
      const onA = itemIsOn(a);
      const onB = itemIsOn(b);
      if (onA !== onB) return onA ? 1 : -1;
      return itemName(a).localeCompare(itemName(b), 'es');
    });
  }
  if (criterion === 'artefacto') {
    return list.sort((a, b) => {
      const aa = itemArtefacto(a);
      const bb = itemArtefacto(b);
      if (!aa && bb) return 1;
      if (aa && !bb) return -1;
      const byType = aa.localeCompare(bb, 'es');
      if (byType !== 0) return byType;
      return itemName(a).localeCompare(itemName(b), 'es');
    });
  }
  return list.sort((a, b) => itemName(a).localeCompare(itemName(b), 'es'));
}

/** Lista unificada Shelly + Sonos + Samsung + Android TV + LG + EvoHome + Pileta, sin ordenar. */
export function collectRoomItems(
  dispositivos: any[],
  sonosList: SonosSpeaker[],
  samsungList: SamsungDevice[] = [],
  androidtvList: AndroidTvDevice[] = [],
  lgList: LgDevice[] = [],
  evohomeList: EvohomeDevice[] = [],
  piletaList: PiletaDevice[] = [],
): RoomListItem[] {
  const items: RoomListItem[] = [
    ...dispositivos.map((d) => ({
      kind: 'device' as const,
      key: d.id as number | string,
      orden: d.orden ?? ORDEN_SIN_ASIGNAR,
      data: d,
    })),
    ...sonosList.map((s) => ({
      kind: 'sonos' as const,
      key: `sonos-${s.id}`,
      orden: s.orden ?? ORDEN_SIN_ASIGNAR,
      data: s,
    })),
    ...samsungList.map((d) => ({
      kind: 'samsung' as const,
      key: `samsung-${d.id}`,
      orden: d.orden ?? ORDEN_SIN_ASIGNAR,
      data: d,
    })),
    ...androidtvList.map((d) => ({
      kind: 'androidtv' as const,
      key: `androidtv-${d.id}`,
      orden: d.orden ?? ORDEN_SIN_ASIGNAR,
      data: d,
    })),
    ...lgList.map((d) => ({
      kind: 'lg' as const,
      key: `lg-${d.id}`,
      orden: d.orden ?? ORDEN_SIN_ASIGNAR,
      data: d,
    })),
    ...evohomeList.map((d) => ({
      kind: 'evohome' as const,
      key: `evohome-${d.id}`,
      orden: d.orden ?? ORDEN_SIN_ASIGNAR,
      data: d,
    })),
    ...piletaList.map((d) => ({
      kind: 'pileta' as const,
      key: `pileta-${d.id}`,
      orden: d.orden ?? ORDEN_SIN_ASIGNAR,
      data: d,
    })),
  ];
  return items;
}

/** Misma lista, con el orden de Ajustes (campo orden de cada tarjeta). */
export function buildMergedRoomItems(
  dispositivos: any[],
  sonosList: SonosSpeaker[],
  samsungList: SamsungDevice[] = [],
  androidtvList: AndroidTvDevice[] = [],
  lgList: LgDevice[] = [],
  evohomeList: EvohomeDevice[] = [],
  piletaList: PiletaDevice[] = [],
): RoomListItem[] {
  return sortByAdminOrden(collectRoomItems(
    dispositivos,
    sonosList,
    samsungList,
    androidtvList,
    lgList,
    evohomeList,
    piletaList,
  ));
}
